import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import IconSourceUpload from '../components/iconGenerator/IconSourceUpload'
import IconCropEditor from '../components/iconGenerator/IconCropEditor'
import IconBackgroundPicker from '../components/iconGenerator/IconBackgroundPicker'
import IconTextStrip from '../components/iconGenerator/IconTextStrip'
import IconPlatformSelector from '../components/iconGenerator/IconPlatformSelector'
import IconDownloadButton from '../components/iconGenerator/IconDownloadButton'
import Seo from '../components/seo/Seo'
import {SITE_URL} from '../components/seo/seoConstants'
import {CtaSection} from '../components/seo/seoSections'
import {trackEvent} from '../../common/analytics/trackEvent'
import {APP_ICON_PLATFORMS, type AppIconPlatformId} from '../../common/constants/appIconSizes'
import type {IconComposeState, IconGradientStop} from '../../common/utils/iconRenderer'
import '../components/seo/SeoPage.css'
import './IconGeneratorPage.css'

const ICON_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Ecranify App Icon Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: `${SITE_URL}/icon-generator`,
  description:
    'Free browser-based app icon generator — one image in, every iOS App Store and Android Play Store icon size out, packaged as a ZIP.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
}

const DEFAULT_GRADIENT: IconGradientStop[] = [
  {id: 'g_a', color: '#7c5cff', position: 0},
  {id: 'g_b', color: '#a78bfa', position: 1},
]

const ALL_PLATFORMS = new Set<AppIconPlatformId>(APP_ICON_PLATFORMS.map((p) => p.id))

const IconGeneratorPage = () => {
  const {t} = useTranslation()

  const [sourceImage, setSourceImage] = useState<HTMLImageElement | null>(null)
  const [zoom, setZoom] = useState(1)
  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)
  const [bgMode, setBgMode] = useState<'color' | 'gradient'>('gradient')
  const [bgColor, setBgColor] = useState('#7c5cff')
  const [bgGradient, setBgGradient] = useState<IconGradientStop[]>(DEFAULT_GRADIENT)
  const [bgAngle, setBgAngle] = useState(180)
  const [textEnabled, setTextEnabled] = useState(false)
  const [textValue, setTextValue] = useState('PRO')
  const [textColor, setTextColor] = useState('#ffffff')
  const [stripColor, setStripColor] = useState('#000000')
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<AppIconPlatformId>>(ALL_PLATFORMS)

  useEffect(() => {
    trackEvent('icon_generator_viewed')
  }, [])

  const state = useMemo<IconComposeState>(
    () => ({
      sourceImage,
      zoom,
      offsetX,
      offsetY,
      bgMode,
      bgColor,
      bgGradient,
      bgAngle,
      textEnabled,
      textValue,
      textColor,
      stripColor,
    }),
    [
      sourceImage,
      zoom,
      offsetX,
      offsetY,
      bgMode,
      bgColor,
      bgGradient,
      bgAngle,
      textEnabled,
      textValue,
      textColor,
      stripColor,
    ],
  )

  const handleOffsetChange = useCallback((nextX: number, nextY: number) => {
    setOffsetX(nextX)
    setOffsetY(nextY)
  }, [])

  const handleResetTransform = useCallback(() => {
    setZoom(1)
    setOffsetX(0)
    setOffsetY(0)
  }, [])

  const handleImageCleared = useCallback(() => {
    setSourceImage(null)
    handleResetTransform()
  }, [handleResetTransform])

  const handleTogglePlatform = useCallback((id: AppIconPlatformId, enabled: boolean) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (enabled) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  return (
    <div className="icg-root seo-root">
      <Seo
        title="Free App Icon Generator — Every iOS & Android Icon Size | Ecranify"
        description="Turn one image into every iOS App Store and Android Play Store app icon size, free, right in your browser. Crop, add a background and label, download a ready-to-use ZIP."
        canonicalPath="/icon-generator"
        jsonLd={[ICON_JSON_LD]}
      />
      <div className="icg-grid" aria-hidden />

      <main className="icg-page">
        <header className="icg-hero">
          <span className="icg-eyebrow">
            <span className="icg-eyebrow-dot" />
            {t('iconGenerator.hero.eyebrow')}
          </span>
          <h1 className="icg-title">
            {t('iconGenerator.hero.titlePrefix')} <em>{t('iconGenerator.hero.titleEm')}</em>
          </h1>
          <p className="icg-lede">{t('iconGenerator.hero.subtitle')}</p>
        </header>

        <div className="icg-layout">
          <section className="icg-col icg-col-source">
            <h2 className="icg-section-title">{t('iconGenerator.sections.source')}</h2>
            <IconSourceUpload image={sourceImage} onImageLoaded={setSourceImage} onClear={handleImageCleared} />
            <h2 className="icg-section-title">{t('iconGenerator.sections.background')}</h2>
            <IconBackgroundPicker
              bgMode={bgMode}
              bgColor={bgColor}
              bgGradient={bgGradient}
              bgAngle={bgAngle}
              onModeChange={setBgMode}
              onColorChange={setBgColor}
              onGradientChange={setBgGradient}
              onAngleChange={setBgAngle}
            />
            <h2 className="icg-section-title">{t('iconGenerator.sections.textStrip')}</h2>
            <IconTextStrip
              enabled={textEnabled}
              textValue={textValue}
              textColor={textColor}
              stripColor={stripColor}
              onEnabledChange={setTextEnabled}
              onTextChange={setTextValue}
              onTextColorChange={setTextColor}
              onStripColorChange={setStripColor}
            />
          </section>

          <section className="icg-col icg-col-preview">
            <h2 className="icg-section-title">{t('iconGenerator.sections.preview')}</h2>
            <IconCropEditor
              state={state}
              onZoomChange={setZoom}
              onOffsetChange={handleOffsetChange}
              onReset={handleResetTransform}
              onImageLoaded={setSourceImage}
            />
            <p className="icg-hint icg-preview-hint">{t('iconGenerator.preview.tip')}</p>
          </section>

          <section className="icg-col icg-col-export">
            <h2 className="icg-section-title">{t('iconGenerator.sections.platforms')}</h2>
            <IconPlatformSelector selected={selectedPlatforms} onToggle={handleTogglePlatform} />
            <IconDownloadButton state={state} selectedPlatforms={selectedPlatforms} />
          </section>
        </div>

        <section className="icg-seo">
          <h2 className="icg-seo-title">{t('iconGenerator.seo.title')}</h2>
          <p className="icg-seo-body">{t('iconGenerator.seo.body1')}</p>
          <p className="icg-seo-body">{t('iconGenerator.seo.body2')}</p>
        </section>

        <CtaSection cta={{title: t('iconGenerator.cta.title'), body: t('iconGenerator.cta.body')}} />
      </main>
    </div>
  )
}

export default IconGeneratorPage
